import {IconButton, InputAdornment, InputProps, TextField as _TextField} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { FC } from 'react';
import {useFormContext} from 'react-hook-form';
import {getValueFromNestedObject} from "../../../../utils/langUtils.ts";
import {DateRange, PickerModal} from "../../daterange";
import {ArrowCircleDown, ArrowCircleRight, ArrowBackIos, ArrowForwardIos, DateRange as DateRangeIcon} from "@mui/icons-material";
import Clear from "@mui/icons-material/Clear";
import {displayDate} from "../../../../utils/formatDate.ts";
import {minus, plus} from "../../../../utils/periodUtils.ts";
import { t } from "@lingui/core/macro";
import { useLocale } from "../../localization/Locale/useLocale";
import {
    addDays,
    startOfWeek,
    endOfWeek,
    addWeeks,
    startOfMonth,
    endOfMonth,
    addMonths,
    startOfYear,
    endOfYear,
    addYears,
    startOfDay,
    endOfDay,
} from "date-fns";

const TextField = styled(_TextField)`
    padding: 0.4rem 0.7rem;
`;

type FormDateRangeProps = {
    displayDateRange?: DateRange;
    initialDateRange?: DateRange;
    minDate?: Date | string;
    maxDate?: Date | string;
    startName: string;
    endName: string;
    startLabel: string;
    endLabel: string;
    clearable?: boolean;
    onChange?: (start: Date | null, end: Date | null) => void;
} & InputProps;

const FormDateRange: FC<FormDateRangeProps> = ({ displayDateRange, initialDateRange, minDate, maxDate, startName, endName, startLabel, endLabel, onChange, clearable = true, ...otherProps }) => {

    const textFieldRef = React.useRef<HTMLInputElement | null>(null);
    const linkRef = React.useRef<HTMLButtonElement | null>(null);
    const { dateFnsLocale } = useLocale()
    const date = new Date()

    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext();

    const startError = getValueFromNestedObject(errors, startName)
    const endError = getValueFromNestedObject(errors, endName)

    const [startValue, endValue] = watch([startName, endName])

    // state + handlers for the Modal
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const handleClick = () => {
        textFieldRef.current?.blur();
        setAnchorEl(linkRef.current);
    };

    const handleClose = React.useCallback(() => {
        if (open) {
            textFieldRef.current?.blur();
        }
        setAnchorEl(null);
    }, [setAnchorEl, anchorEl, textFieldRef]);

    const open = Boolean(anchorEl);

    const handleSetDateRangeOnChange =  React.useCallback((dateRange: DateRange) => {
        setValue(startName, dateRange.startDate)
        setValue(endName, dateRange.endDate)
    }, [setValue]);

    const handleMinus = React.useCallback(() => {
        const result = minus(startValue, endValue)
        setValue(startName, result.startDate)
        setValue(endName, result.endDate)
    }, [startValue, endValue]);

    const handlePlus = React.useCallback(() => {
        const result = plus(startValue, endValue)
        setValue(startName, result.startDate)
        setValue(endName, result.endDate)
    }, [startValue, endValue]);

    const handleClear = React.useCallback(() => {
        handleClose()
        setValue(startName, null)
        setValue(endName, null)
    }, [setValue, handleClose]);

    return React.useMemo(() => (
        <>
            <TextField
                inputRef={textFieldRef}
                fullWidth={true}
                margin="normal"
                error={!!startError || !!endError}
                variant="standard"
                label={`${startLabel} - ${endLabel}`}
                value={startValue || endValue ? `${displayDate(startValue)} - ${displayDate(endValue)}` : ''}
                onFocus={handleClick}
                onChange={() => {
                    onChange && onChange(startValue, endValue)
                }}
                slotProps={
                    {
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    { startValue &&
                                        <IconButton
                                            onClick={handleMinus}
                                            edge="start"
                                        >
                                            <ArrowBackIos/>
                                        </IconButton>
                                    }
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    { clearable && startValue &&
                                        <IconButton
                                            onClick={handleClear}
                                            edge="end"
                                        >
                                            <Clear />
                                        </IconButton>
                                    }
                                    <IconButton
                                        onClick={handleClick}
                                        ref={linkRef}
                                        edge="end"
                                    >
                                        <DateRangeIcon />
                                    </IconButton>
                                    { endValue &&
                                        <IconButton
                                            onClick={handlePlus}
                                            edge="end"
                                        >
                                            <ArrowForwardIos/>
                                        </IconButton>
                                    }
                                </InputAdornment>
                            ),

                        },
                    }
                }
                helperText={
                    startError ? (startError?.message as unknown as string) : ''
                }
                {...otherProps}
            />
            <PickerModal
                hideOutsideMonthDays={true}
                hideSubmitButton={true}
                minDate={minDate}
                maxDate={maxDate}
                displayDateRange={{
                    startDate: displayDateRange?.startDate || new Date(),
                    endDate: displayDateRange?.endDate || new Date(),
                }}
                initialDateRange={{
                    startDate: startValue || initialDateRange?.startDate,
                    endDate: endValue || initialDateRange?.endDate,
                }}
                onChange={(range: DateRange) => handleSetDateRangeOnChange(range)}
                customProps={{
                    onCloseCallback: handleClose,
                    RangeSeparatorIcons: {
                        xs: ArrowCircleDown,
                        md: ArrowCircleRight,
                    },
                }}
                locale={dateFnsLocale}
                labels={{
                    predefinedRanges: t`Quick Select`,
                    actions: {
                        apply: t`Apply`,
                        cancel: t`Close`,
                    },
                    footer: {
                        startDate: t`Start Date`,
                        endDate: t`End Date`,
                    }
                }}
                definedRanges={
                    [
                        {
                            label: t`Today`,
                            startDate: startOfDay(date),
                            endDate: endOfDay(date),
                        },
                        {
                            label: t`Yesterday`,
                            startDate: startOfDay(addDays(date, -1)),
                            endDate: endOfDay(addDays(date, -1)),
                        },
                        {
                            label: t`This Week`,
                            startDate: startOfWeek(date, { locale: dateFnsLocale }),
                            endDate: endOfWeek(date, { locale: dateFnsLocale }),
                        },
                        {
                            label: t`Last Week`,
                            startDate: startOfWeek(addWeeks(date, -1), { locale: dateFnsLocale }),
                            endDate: endOfWeek(addWeeks(date, -1), { locale: dateFnsLocale }),
                        },
                        {
                            label: t`This Month`,
                            startDate: startOfMonth(date),
                            endDate: endOfMonth(date),
                        },
                        {
                            label: t`Last Month`,
                            startDate: startOfMonth(addMonths(date, -1)),
                            endDate: endOfMonth(addMonths(date, -1)),
                        },
                        {
                            label: t`This Year`,
                            startDate: startOfYear(date),
                            endDate: endOfYear(date),
                        },
                        {
                            label: t`Last Year`,
                            startDate: startOfYear(addYears(date, -1)),
                            endDate: endOfYear(addYears(date, -1)),
                        },
                    ]
                }
                modalProps={{
                    open,
                    anchorEl,
                    onClose: handleClose,
                    slotProps: {
                        paper: {
                            sx: {
                                borderRadius: "16px",
                                boxShadow: "rgba(0, 0, 0, 0.21) 0px 0px 4px",
                            },
                        },
                    },
                    anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                    },
                }}
            />
        </>
    ), [startError, endError, startLabel, endLabel, startValue, endValue, open, anchorEl, handleClose, handleSetDateRangeOnChange, handleClick, handleClear])
};

export default FormDateRange;
